import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["manualMode", "amountCents", "manualForm"]
  static values = {
    insurancePolicyId: String,
    switchUrl: String,
    skipUrl: String
  }

  async prepareManual() {
    try {
      await this.switchOnServer("ledger_manual")
    } catch (error) {
      this.showError(error.message)
    }
  }

  async prepareOneShot() {
    await this.switchOnServer("ledger_pay")
    this.element.dispatchEvent(
      new CustomEvent("ledger-billing-alternatives:show-one-shot", { bubbles: true })
    )
  }

  selectManualMode(event) {
    const mode = event.currentTarget.dataset.mode
    if (!mode) return

    this.manualModeTarget.value = mode
    this.element.querySelectorAll(".ledger-billing-manual-mode-btn").forEach((button) => {
      button.classList.toggle("active", button.dataset.mode === mode)
    })
  }

  async submitManual(event) {
    event.preventDefault()
    this.clearError()

    const form = this.manualFormTarget
    const submitButton = form.querySelector("[type=submit]")
    if (submitButton) submitButton.disabled = true

    try {
      await this.switchOnServer("ledger_manual")

      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "application/json"
        },
        body: new FormData(form)
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || "Impossible d'enregistrer l'encaissement")
      }

      this.closeModalAndRefresh()
    } catch (error) {
      this.showError(error.message)
      if (submitButton) submitButton.disabled = false
    }
  }

  async skipStep(event) {
    event.preventDefault()
    const button = event.currentTarget
    const confirmMessage = button.dataset.turboConfirm
    if (confirmMessage && !window.confirm(confirmMessage)) return

    button.disabled = true

    try {
      const response = await fetch(this.skipUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "application/json"
        },
        body: JSON.stringify({
          global_workflow: new URL(window.location.href).searchParams.get("global_workflow")
        })
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || "Impossible de passer l'étape")
      }

      this.submitLedgerBillingPaymentStep()
    } catch (error) {
      button.disabled = false
      this.showError(error.message)
    }
  }

  async switchOnServer(mode) {
    const response = await fetch(this.switchUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json"
      },
      body: JSON.stringify({ mode })
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || "Impossible de préparer le paiement")
    }

    this.updateBalances(data)
    return data
  }

  updateBalances(data) {
    if (!data.remaining_formatted) return

    document.querySelectorAll("[data-ledger-balance]").forEach((node) => {
      node.textContent = data.remaining_formatted
    })

    document.querySelectorAll("[data-ledger-amount-cents]").forEach((input) => {
      input.value = data.remaining_cents
    })
  }

  closeModalAndRefresh() {
    const modal = this.hasManualFormTarget ? this.manualFormTarget.closest(".modal") : this.element.closest(".modal")
    if (!modal) {
      this.refreshPaymentStep()
      return
    }

    let refreshed = false
    const refreshOnce = () => {
      if (refreshed) return
      refreshed = true
      modal.removeEventListener("hidden.bs.modal", refreshOnce)
      this.cleanupModalArtifacts()
      this.refreshPaymentStep()
    }
    modal.addEventListener("hidden.bs.modal", refreshOnce)

    const instance = Modal.getInstance(modal)
    if (instance) {
      instance.hide()
      setTimeout(refreshOnce, 400)
      return
    }

    refreshOnce()
  }

  refreshPaymentStep() {
    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
      return
    }

    window.location.reload()
  }

  cleanupModalArtifacts() {
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())
  }

  showError(message) {
    this.clearError()
    const alert = document.createElement("div")
    alert.className = "alert alert-danger py-2 small mb-2"
    alert.setAttribute("data-ledger-billing-alternatives-error", "")
    alert.textContent = message

    const modalBody = this.hasManualFormTarget ? this.manualFormTarget.closest(".modal-body") : null
    if (modalBody) {
      modalBody.prepend(alert)
    } else {
      this.element.prepend(alert)
    }
  }

  clearError() {
    document.querySelectorAll("[data-ledger-billing-alternatives-error]").forEach((node) => node.remove())
  }

  submitLedgerBillingPaymentStep() {
    const form = document.querySelector("form[data-ledger-billing-step-form]")
    if (!form) {
      throw new Error("Formulaire paiement introuvable")
    }

    const submit = form.querySelector("[data-payment-methods-target='subscribeButton']")
    if (submit) submit.disabled = false
    form.requestSubmit()
  }

  getCSRFToken() {
    const csrfMeta = document.querySelector("[name=\"csrf-token\"]")
    if (csrfMeta?.content) return csrfMeta.content
    const input = document.querySelector("input[name=\"authenticity_token\"]")
    return input?.value || ""
  }
}
